import { useContext, useState } from 'react'

import {
  tracerError,
  useCoordinator,
  useLocalUser,
} from '@builddocs/data-management'
import { PaywallContext } from '@builddocs/system'
import { useMutation, useQueryClient } from 'react-query'

import {
  billingEngineMutations,
  CheckOperationResponse,
  GroupedOperation,
} from './billing-engine-queries'
import { OperationType } from './operations'

interface ICheckAndCommitOperation<T> {
  onExceeded: (operationInfo?: {
    isAllowed: boolean
    requestedLimit: number
    availableLimit: number
  }) => void
  onSuccess?: () => void
  operation: {
    operationId:
      | 'CREATE_STAMP'
      | 'SEND_DOC_TO_CHECK'
      | 'BATCH_UPLOAD_DOCS_TO_DOCUMENTARY'
    groupedOperations?: GroupedOperation[]
    operationCallback: () =>
      | Promise<T | { skipped?: boolean; operationCount?: number }>
      | T
    operationCount: number
  }
  options?: {
    skipCommit?: boolean
  }
}

export function useCheckAndCommitOperation() {
  const queryClient = useQueryClient()
  const user = useLocalUser()
  const { projectId = '' } = useCoordinator()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const { mutateAsync: checkOperation } = useMutation(
    billingEngineMutations.checkOperation(),
  )
  const { mutateAsync: commitOperation } = useMutation(
    billingEngineMutations.commitOperation(),
  )

  const checkAndCommitOperation = async <T>({
    onExceeded,
    onSuccess,
    operation,
    options,
  }: ICheckAndCommitOperation<T>) => {
    setIsLoading(true)
    setIsError(false)

    try {
      const operationInfo = await checkOperation({
        operationId: operation.operationId,
        body: {
          projectId,
          operationCount: operation.operationCount,
          groupedOperations: operation.groupedOperations,
        },
      })

      if (operationInfo.isAllowed) {
        const result = await operation.operationCallback()

        // @ts-expect-error не знаю как это решить
        // Костыль чтобы скипать commitOperation для отменных запросов
        if (result?.skipped) {
          return
        }

        if (!options?.skipCommit) {
          await commitOperation({
            projectId,
            operationId: operation.operationId,
            // @ts-expect-error костыль, чтобы считаь только реальные операции при отправке на согласование
            operationCount: result?.operationCount ?? operation.operationCount,
            groupedOperations: operation.groupedOperations,
          })
          queryClient.invalidateQueries({
            queryKey: ['billing-engine', 'available-limit', projectId],
          })
        }
        onSuccess?.()
      } else {
        console.log('operationInfo', operationInfo)
        onExceeded(operationInfo)
      }
    } catch (e) {
      setIsError(true)
      tracerError.error(e, {
        component: 'useCheckAndCommitOperation',
        userId: user?.userId ?? 'UNDEFINED',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return { checkAndCommitOperation, isLoading, isError }
}

let DISABLE_VERIFICATIONS = false

const changeDisabledVerifications = () => {
  DISABLE_VERIFICATIONS = !DISABLE_VERIFICATIONS
}

;(window as any).changeDisabledVerifications = changeDisabledVerifications

/**
 *
 *  @description Хук для проверки readonly операций
 *  @example
 *  const { checkOperation } = useCheckOperation()
 *  checkOperation({
 *    operationId: 'SEND_DOC_TO_CHECK',
 *    operationCallback: () => {
 *      console.log('Operation allowed')
 *    },
 *  })
 */
export function useCheckOperation() {
  const user = useLocalUser()
  const { projectId = '' } = useCoordinator()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const { mutateAsync: checkOperationMutation } = useMutation(
    billingEngineMutations.checkOperation(),
  )
  const { open: openPaywall } = useContext(PaywallContext)

  const checkOperation = async ({
    operationId,
    count,
    operationCallback,
    onExceeded,
  }: {
    operationId: OperationType
    count?: number
    operationCallback?: () => Promise<void> | void
    onExceeded?: (operationInfo: CheckOperationResponse) => void
  }) => {
    setIsLoading(true)
    setIsError(false)

    try {
      const operationInfo = await checkOperationMutation({
        operationId,
        body: {
          projectId,
          operationCount: count ?? 1,
        },
      })

      if (!operationInfo.isAllowed && !DISABLE_VERIFICATIONS) {
        try {
          onExceeded?.(operationInfo)
        } catch (e) {
          tracerError.error(e, {
            component: 'useCheckOperation',
            userId: user?.userId ?? 'UNDEFINED',
            severity: 'error',
            keys: {
              operationId,
              projectId,
            },
          })
        }
        openPaywall({ ...operationInfo, isReadOnly: true })
        return new Promise<never>((_, reject) => {
          reject(new Error('Operation not allowed'))
        })
      }

      return operationCallback?.()
    } catch (e) {
      setIsError(true)
      tracerError.error(e, {
        component: 'useCheckOperation',
        userId: user?.userId ?? 'UNDEFINED',
        severity: 'error',
        keys: {
          operationId,
          projectId,
        },
      })
    } finally {
      setIsLoading(false)
    }
  }

  return { checkOperation, isLoading, isError }
}
